import isPreviewBuild from "./isPreviewBuild";

const CONTENT_CACHE = {};
const IS_PREVIEW = isPreviewBuild();
const IS_DEV = import.meta.env.DEV;

async function fromCache(key, fn, cache = CONTENT_CACHE) {
  if (!cache[key]) {
    cache[key] = await fn();
    if (key === "pageMap") {
      // eslint-disable-next-line no-console
      console.log(`BUILD PAGE MAP LENGTH: ${Object.keys(cache[key]).length}`);
    }
  }
  return cache[key];
}

// eslint-disable-next-line no-console
function log(status, params, fn = console.log) {
  if (params.relativePath) {
    fn(`${status} fetching content for relativePath ${params.relativePath}`);
  } else {
    fn(`${status} fetching content for ${params.url}`);
  }
}

async function getContent(endpoint, params = {}, attempt = 1) {
  const relativePath = params.body ? JSON.parse(params.body).relativeUrl : null;
  const root = IS_PREVIEW
    ? import.meta.env.VITE_PREVIEW_API_URL || import.meta.env.VITE_API_URL
    : import.meta.env.VITE_API_URL;
  const url = `${root}${endpoint}${IS_PREVIEW ? "?preview=true" : ""}`;
  let response = await fetch(url, params);
  // removing logs from here because we throw the error, which logs it anyway.
  if (response.status === 200) {
    // eslint-disable-next-line no-console
    // log("SUCCESS", { relativePath, url }); // don't log success
    return response.json();
  } else if (response.status === 404) {
    // eslint-disable-next-line no-console
    // log("Contentful 404 ERROR", { relativePath, url }, console.error);
    throw Error("contentful-api-fn 404");
  } else if (attempt < 4) {
    // TODO: is this necessary?
    // if it gives us a 200 or 404, we dont retry
    // eslint-disable-next-line no-console
    // console.log(`Try ${attempt} to ${endpoint} failed. Retrying.`);
    return await getContent(endpoint, params, attempt + 1);
    // recursive call
  } else if (response.status === 500) {
    // eslint-disable-next-line no-console
    // log("Contentful 500 ERROR", { relativePath, url }, console.error);
    throw Error("contentful-api-fn 500");
  } else {
    let json;
    try {
      json = await response.json();
    } catch {
      // eslint-disable-next-line no-console
      log("ERROR", { relativePath: url, response }, console.error);
      throw Error("Unparseable response from contentful fn api");
    }

    // eslint-disable-next-line no-console
    log("ERROR", { relativePath, url }, console.error);
    throw Error(json.stack ? json.stack : json.message);
  }
}

export async function getPage(page) {
  const urlPath = page ? `/${page}` : "/";
  return await getPageByPath(urlPath);
}

export async function getPageByPath(urlPath) {
  if (IS_PREVIEW || IS_DEV) {
    return await getContent("/api/v4/page-by-path", {
      method: "POST",
      body: JSON.stringify({ relativeUrl: urlPath })
    });
  } else {
    return await fromCache(urlPath, async () =>
      getContent("/api/v4/page-by-path", {
        method: "POST",
        body: JSON.stringify({
          relativeUrl: urlPath,
          fullBuild: true,
          buildId: import.meta.env.VITE_BUILD_ID
        })
      })
    );
  }
}

export async function getContributors() {
  if (IS_PREVIEW) {
    return await getContent("/api/contributors");
  } else {
    return await fromCache("contributors", async () => getContent("/api/contributors"));
  }
}

export async function getVirtualSupporters() {
  if (IS_PREVIEW) {
    return await getContent("/api/virtual-supporters");
  } else {
    return await fromCache("virtualSupporters", async () => getContent("/api/virtual-supporters"));
  }
}

export async function getBanners() {
  if (IS_PREVIEW) {
    return await getContent("/api/banner");
  } else {
    return await fromCache("banner", async () => getContent("/api/banner"));
  }
}

export async function getFeatureFlags() {
  if (IS_PREVIEW) {
    return await getContent("/api/feature-flags");
  } else {
    return await fromCache("feature-flags", async () => getContent("/api/feature-flags"));
  }
}

export async function getErrorPage() {
  const urlPath = import.meta.env.VITE_CONTENTFUL_ERROR_PAGE || "error/";
  if (IS_PREVIEW) {
    return await getPage(urlPath);
  } else {
    return await fromCache("error", async () => getPage(urlPath));
  }
}

export async function getEvent(slug) {
  return await getContent(`/api/event/${slug}`);
}

export async function getEvents() {
  if (IS_PREVIEW) {
    // pageMap_v2 will only be called when request for siteMap, under preview mode
    return await getContent("/api/eventsList");
  } else {
    return await fromCache("eventsList", async () => getContent("/api/eventsList"));
  }
}

export async function getNavigation() {
  if (IS_PREVIEW) {
    return await getContent("/api/navigation");
  } else {
    return await fromCache("navigation", async () => getContent("/api/navigation"));
  }
}

export async function getPages() {
  if (IS_PREVIEW) {
    // pageMap_v2 will only be called when request for siteMap, under preview mode
    return await getContent("/api/v4/sitemap");
  } else {
    return await fromCache("pageDataList", async () => getContent("/api/v4/sitemap"));
  }
}

export async function getRedirectRoutes() {
  if (IS_PREVIEW) {
    return await getContent("/api/redirects");
  } else {
    return await fromCache("redirects", async () => getContent("/api/redirects"));
  }
}

export async function getFruRedirectRoutes() {
  if (IS_PREVIEW) {
    return await getContent("/api/fru-redirects");
  } else {
    return await fromCache("fru-redirects", async () => getContent("/api/fru-redirects"));
  }
}
